import type { ToolkitStore } from '@reduxjs/toolkit/src/configureStore';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isPlainObject from 'lodash/isPlainObject';
import mergeWith from 'lodash/mergeWith';
import set from 'lodash/set';
import unset from 'lodash/unset';

import type { ConvertObjToDots } from 'commonUtils/convertObjectToDots';
import { ERROR_BOUNDARY_RETRY_QUERY_PARAM } from 'constants/queryNames';

import { localStorageKeys } from 'shared/config/localStorageKeys';
import { addPathNameSlash } from 'shared/lib/addPathNameSlash';
import { customSessionStorage } from 'shared/lib/customStorage';
import { sendSentryClientError } from 'shared/lib/sendSentryClientError';

import type { GlobalState } from '../../model/store';
import { getOrCreateStore } from '../../model/store';

import { AUTH_PERSISTED_ROUTES, BASE_PERSISTED_ROUTES } from './PersistedStore.config';
import type { TPersistedConfigByRoute } from './types';

export interface IPersistedStore {
  save: () => void;
  getStore: () => ToolkitStore<GlobalState>;
}

/**
 * При загрузке страницы мы создаем стор и часть данных получаем при серверном рендеринге
 * Данный класс управляет объемом данных которые клиент восстанавливает в стор из sessionStorage
 * */
export class PersistedStore implements IPersistedStore {
  // указатель на созданный стор
  private readonly store: ToolkitStore<GlobalState>;

  private includePaths: Array<ConvertObjToDots<GlobalState>> = [];
  private excludePaths: Array<ConvertObjToDots<GlobalState>> = [];

  constructor(defaultValue: GlobalState, isAuth: boolean, additionalPathsConfig?: Partial<TPersistedConfigByRoute>) {
    if (typeof window === 'undefined') {
      this.store = getOrCreateStore(defaultValue);
      return;
    }

    this.configurePersistedPaths(isAuth, additionalPathsConfig);

    const params = new URLSearchParams(window.location.search);
    const errorBoundaryRetryQuery = params.get(ERROR_BOUNDARY_RETRY_QUERY_PARAM);

    if (this.includePaths?.length && !errorBoundaryRetryQuery) {
      this.store = getOrCreateStore(this._restore(defaultValue));
    } else {
      this.store = getOrCreateStore(defaultValue);
    }
  }

  /**
   * Конфигурирует пути для сохранения/восстановления данных стора в зависимости от текущего роута и авторизации.
   * Для каждого роута определяет include/exclude поля, которые будут сохраняться в sessionStorage.
   * @param isAuth Флаг авторизации пользователя.
   * @param additionalConfig Дополнительная конфигурация путей для конкретных роутов.
   */
  configurePersistedPaths = (isAuth: boolean, additionalConfig?: Partial<TPersistedConfigByRoute>) => {
    if (typeof window === 'undefined') {
      return;
    }

    const currentPath = addPathNameSlash(window.location.pathname);

    const routesConfigs: Partial<TPersistedConfigByRoute> = {
      ...BASE_PERSISTED_ROUTES,
      ...(isAuth ? AUTH_PERSISTED_ROUTES : {}),
      ...(additionalConfig || {}),
    };

    const currentRouteConfig = routesConfigs[currentPath];

    if (Array.isArray(currentRouteConfig)) {
      this.includePaths = currentRouteConfig;
    } else if (currentRouteConfig) {
      this.includePaths = currentRouteConfig.include;
      this.excludePaths = currentRouteConfig.exclude || [];
    }
  };

  // получить созданный ранее стор
  getStore(): ToolkitStore<GlobalState> {
    return this.store;
  }

  /**
   * Метод сохраняет данные в локальное хранилище из redux store
   * */
  save = (): void => {
    try {
      this._writeJSON(this._encodeValue(this.store.getState()));
    } catch (e) {
      sendSentryClientError(e as Error, { placement: 'PersistedStore.save' });
    }
  };

  // Очистка persisted данных в sessionStorage
  clear = (): void => {
    try {
      this._clearJSON();
    } catch (e) {
      sendSentryClientError(e as Error, { placement: 'PersistedStore.clear' });
    }
  };

  /**
   * Восстанавливает состояние по include/exclude путям из sessionStorage.
   *
   * Алгоритм:
   * 1. Читает JSON (`_readJSON` + `_decodeValue`).
   * 2. Для каждого include-пути берёт сохранённое значение.
   * 3. Рекурсивно мерджит с дефолтным (`_mergePreserving`) не теряя новых ключей.
   * 4. Находит относительные exclude (начинаются с `${includePath}.`) и вырезает их (`_pruneExcluded`).
   * 5. Записывает результат в копию стора.
   *
   * Если данных нет или ошибка парсинга — возвращает `defaultValue`.
   *
   * @param defaultValue Полное дефолтное состояние (SSR).
   * @returns Состояние после восстановления.
   *
   * @example
   * ```ts
   * // Конфиг:
   * include: ['creditForm.contactsForm.data', 'creditForm.creditPurposeForm.data']
   * exclude: ['creditForm.contactsForm.data.fullName']
   * defaultValue.contactsForm.data = { fullName: null, email: '' }
   * // persisted JSON:
   * {
   *  "creditForm": {
   *    "contactsForm": { "data": { "fullName": { "value": "X" }, "email": "test@mail.examle" } }
   *   }
   * }
   * // Результат:
   *  creditForm.contactsForm.data = { fullName: null, email: 'test@mail.examle' }
   */
  private _restore = (defaultValue: GlobalState): GlobalState => {
    try {
      const restoredData = this._decodeValue(this._readJSON());

      if (!Object.keys(restoredData).length) {
        return defaultValue;
      }

      // Копия стора (корневой уровень), вложенные объекты сохраняются
      const result: GlobalState = { ...defaultValue };

      this.includePaths?.forEach((p) => {
        const path = p as string;
        const persistedVal = get(restoredData, path);
        if (persistedVal === undefined) {
          return;
        }

        const defaultVal = get(defaultValue, path);
        const merged = this._mergePreserving(defaultVal, persistedVal);

        // относительные exclude внутри include-пути
        const relativeExclude = this.excludePaths
          .filter((ex) => ex.startsWith(`${path}.`))
          .map((ex) => ex.slice(path.length + 1));

        if (relativeExclude.length) {
          relativeExclude.forEach((rel) => {
            const defaultExcluded = get(defaultVal, rel);
            // восстанавливаем дефолт вместо потери поля
            if (defaultExcluded !== undefined && merged && typeof merged === 'object') {
              set(merged, rel, defaultExcluded);
            }
          });
        }

        set(result, path, merged);
      });

      return result;
    } catch (e) {
      sendSentryClientError(e as Error, { placement: 'PersistedStore._restore' });
      return defaultValue;
    }
  };

  private _readJSON(): string {
    const result = customSessionStorage.get(localStorageKeys.appState) ?? '';
    customSessionStorage.remove(localStorageKeys.appState);
    return result;
  }

  private _writeJSON(value: string): void {
    customSessionStorage.set(localStorageKeys.appState, value);
  }

  private _clearJSON(): void {
    customSessionStorage.remove(localStorageKeys.appState);
  }

  /**
   * Кодирует текущее состояние стора в JSON, выбирая только пути из include
   * и вырезая относительные подпути из exclude.
   *
   * Алгоритм:
   * 1. Проходит по include-путям.
   * 2. Берёт значение по dot-path.
   * 3. Находит относительные exclude (начинаются с `${includePath}.`).
   * 4. Удаляет их через `_pruneExcluded`.
   * 5. Собирает объект и делает `JSON.stringify`.
   *
   * @example
   * ```ts
   * include: ['creditForm.creditPurposeForm.data']
   * exclude: ['creditForm.creditPurposeForm.data.autoBrand']
   * state.creditForm.creditPurposeForm.data = {
   *   autoBrand: { label: 'A' },
   *   amount: '100000',
   *   period: '12'
   * };
   * // Результат JSON:
   * {
   * "creditForm": {
   *  "creditPurposeForm": {
   *    "data": { "amount": "100000", "period": "12" }
   *   }
   *  }
   * }
   */
  private _encodeValue(state: GlobalState): string {
    const persistedData: Record<string, unknown> = {};

    this.includePaths?.forEach((p) => {
      const path = p as string;
      let value = get(state, path);
      if (value !== undefined) {
        // исключения, которые лежат глубже include-пути
        const relativeExclude = this.excludePaths
          .filter((ex) => ex.startsWith(`${path}.`))
          .map((ex) => ex.slice(path.length + 1));

        if (relativeExclude.length) {
          value = this._pruneExcluded(value, relativeExclude);
        }
        set(persistedData, path, value);
      }
    });

    return JSON.stringify(persistedData);
  }

  private _decodeValue(value: string): Partial<GlobalState> {
    try {
      return JSON.parse(value);
    } catch (e) {
      return {};
    }
  }

  /**
   * Глубоко объединяет `defaultVal` и `persistedVal`:
   * - Если `persistedVal === undefined`, возвращает `defaultVal`.
   * - Если хоть одно из значений не `plain object`, возвращает `persistedVal` как итог.
   * - Plain-объекты мерджатся рекурсивно, отсутствующие ключи из `defaultVal` сохраняются.
   * - Массивы не сливаются по индексам, а полностью заменяются массивом из `persistedVal`.
   *
   * Используется для восстановления сохранённых веток без потери новых (дефолтных) полей.
   *
   * @example
   * ```ts
   * const defaultVal = {
   *   a: 1,
   *   obj: { x: 1, y: 2 },
   *   arr: [1, 2],
   *   nested: { k: { z: 1, keep: true } }
   * };
   *
   * const persistedVal = {
   *   a: 5,
   *   obj: { x: 10 },
   *   arr: [9],
   *   nested: { k: { z: 2 } }
   * };
   *
   * // Результат:
   * {
   *  a: 5,
   *  obj: { x: 10, y: 2 },
   *  arr: [9],
   *  nested: { k: { z: 2, keep: true } }
   * }
   */
  private _mergePreserving<T>(defaultVal: T, persistedVal: unknown): T {
    if (persistedVal === undefined) return defaultVal;
    if (!isPlainObject(defaultVal) || !isPlainObject(persistedVal)) {
      return persistedVal as T;
    }
    return mergeWith({}, defaultVal, persistedVal, (objValue, srcValue) => {
      // Массивы не сливаем по индексам, а заменяем
      if (Array.isArray(objValue) && Array.isArray(srcValue)) {
        return srcValue; // полная замена массива
      }
      return undefined; // стандартный merge для остальных случаев
    }) as T;
  }

  /**
   * Возвращает глубоко клонированное значение без относительных путей `relPaths`.
   * Каждый путь относительный (без include-префикса).
   *
   * Правила:
   * - Если `value` не объект (primitive или null) — возвращается как есть.
   * - Для каждого пути из `relPaths` выполняется удаление через `unset`.
   *
   * @param value Исходное значение.
   * @param relPaths Массив относительных dot-path путей для удаления.
   * @returns Новое значение без указанных путей.
   *
   * @example
   * ```ts
   * const value = {
   *   auto: { brand: 'A', model: 'M', nested: { year: 2024 } },
   *   keep: 1
   * };
   * // Удаляем auto.model и auto.nested.year
   * const pruned = this._pruneExcluded(value, ['auto.model', 'auto.nested.year']);
   * // Результат:
   * {
   *  auto: { brand: 'A', nested: {} },
   *  keep: 1
   * }
   */
  private _pruneExcluded(value: unknown, relPaths: string[]): unknown {
    if (value == null || typeof value !== 'object') return value;
    const clone = cloneDeep(value);
    relPaths.forEach((p) => unset(clone as any, p));
    return clone;
  }
}
